import React, { useState, useEffect, useRef, useContext } from 'react';

import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Divider from '@amzn/meridian/divider';
import Icon from '@amzn/meridian/icon';
import Loader from '@amzn/meridian/loader';
import Popover from '@amzn/meridian/popover';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import closeSmallTokens from '@amzn/meridian-tokens/base/icon/close-small';
import infoKnockoutTokens from '@amzn/meridian-tokens/base/icon/info-knockout';

import moment from 'moment';
import {
  chainWalk,
  convertEpochToDatetimeLocal,
  parseEpochToDateStringWithOptions,
  parseEpochToHourMinute
} from '../utils/helpers';

import ShiftAdherenceSelector from './ShiftAdherenceSelector';
import ShiftAdherenceGraph from './ShiftAdherenceGraph';
import ShiftAdherenceTable from './ShiftAdherenceTable';
import ShiftAdherencePlansView from './ShiftAdherencePlansView';
import { getAdminSettings, checkNarrowWindowsEnabled } from '../utils/AdminSettings';
import { getStationCode } from '../utils/networkUtil';
import { SSD_FEATURE_FLAGS_LIST, GROCERY_PROCESS_PATH_TO_ROLES } from '../utils/constants';

// Number of plans to show on the UI.
const NUM_PLANS = 5;

// Filtering functions for grocery roles with data
export function hasGroceryDataForProcessPath(processPathName, adherenceData) {
  if (!GROCERY_PROCESS_PATH_TO_ROLES[processPathName]) {
    return false;
  }

  const groceryRoles = Object.keys(GROCERY_PROCESS_PATH_TO_ROLES[processPathName]);

  return chainWalk(() => adherenceData['timeWindowHeadcountAdherences'], []).some((timeWindowAdherence) => {
    return timeWindowAdherence['roleHeadcountAdherences'].some((roleAdherence) => {
      const roleName = chainWalk(
        () => roleAdherence['entityAttrs']['name'].split('__')[2],
        roleAdherence['entityAttrs']['name']
      );
      return groceryRoles.includes(roleName);
    });
  });
}

export function getGroceryRolesWithData(processPathName, adherenceData) {
  if (!GROCERY_PROCESS_PATH_TO_ROLES[processPathName]) {
    return [];
  }

  const groceryRoles = Object.keys(GROCERY_PROCESS_PATH_TO_ROLES[processPathName]);
  const rolesWithData = [];

  chainWalk(() => adherenceData['timeWindowHeadcountAdherences'], []).forEach((timeWindowAdherence) => {
    timeWindowAdherence['roleHeadcountAdherences'].forEach((roleAdherence) => {
      const roleName = chainWalk(
        () => roleAdherence['entityAttrs']['name'].split('__')[2],
        roleAdherence['entityAttrs']['name']
      );
      if (groceryRoles.includes(roleName) && !rolesWithData.includes(roleName)) {
        rolesWithData.push(roleName);
      }
    });
  });

  return rolesWithData;
}

const generateWindowsFromOpsClock = (
  ssdAdminSettings,
  currDate,
  previousDate,
  updatedStationTimeZone,
  stationTimeZoneOffset
) => {
  const opsClock = chainWalk(() => ssdAdminSettings['ops_clock'], []);
  const astEntry = opsClock.find((entry) => entry.name === 'ast');
  const finalStageEntry = opsClock.find((entry) => entry.name === 'finalstage');
  const astWindows = chainWalk(() => astEntry['window_list'], []);
  const finalStageWindows = chainWalk(() => finalStageEntry['window_list'], []);

  if (astWindows.length === 0 || finalStageWindows.length === 0) {
    return {};
  }

  const windows = [];
  [currDate, previousDate].forEach((day) => {
    const dateTimeStringLocal = convertEpochToDatetimeLocal(day.getTime() / 1000, updatedStationTimeZone);
    const dateLocal = dateTimeStringLocal.split('T')[0];

    astWindows.forEach((astWindow, index) => {
      const finalStageWindow = finalStageWindows[index];
      if (!astWindow.time || !finalStageWindow || !finalStageWindow.time) return;

      const windowStartUtc = new Date(dateLocal + 'T' + astWindow.time + stationTimeZoneOffset);
      let windowEndUtc = new Date(dateLocal + 'T' + finalStageWindow.time + stationTimeZoneOffset);

      if (windowEndUtc.getTime() <= windowStartUtc.getTime()) {
        windowEndUtc.setDate(windowEndUtc.getDate() + 1);
      }

      if (windowEndUtc.getTime() < currDate.getTime()) {
        const startTimeStr = parseEpochToHourMinute(windowStartUtc.getTime(), updatedStationTimeZone);
        const endTimeStr = parseEpochToHourMinute(windowEndUtc.getTime(), updatedStationTimeZone);
        windows.push({
          label: `${astWindow.name} (${startTimeStr} - ${endTimeStr})`,
          start: windowStartUtc,
          end: windowEndUtc
        });
      }
    });
  });

  windows.sort((a, b) => b.end.getTime() - a.end.getTime());
  const result = {};
  windows.slice(0, NUM_PLANS).forEach((window) => {
    result[window.label] = { start: window.start, end: window.end };
  });
  return result;
};

const generateShiftsFromConfig = (shifts, currDate, previousDate, updatedStationTimeZone, stationTimeZoneOffset) => {
  const allAvailableShifts = {};
  [currDate, previousDate].forEach((day) => {
    const dateTimeStringLocal = convertEpochToDatetimeLocal(day.getTime() / 1000, updatedStationTimeZone);
    const dateLocal = dateTimeStringLocal.split('T')[0];

    shifts.forEach((shift) => {
      const shiftStartDateUtc = new Date(dateLocal + 'T' + shift['start'] + stationTimeZoneOffset);
      const shiftEndDateUtc = new Date(dateLocal + 'T' + shift['end'] + stationTimeZoneOffset);

      if (shiftEndDateUtc.getTime() < shiftStartDateUtc.getTime()) {
        shiftEndDateUtc.setDate(shiftEndDateUtc.getDate() + 1);
      }

      if (shiftStartDateUtc.getTime() < currDate.getTime()) {
        const shiftDisplayString = `${shift['label']} (${
          parseEpochToDateStringWithOptions(shiftStartDateUtc.getTime() / 1000, {
            timeZone: updatedStationTimeZone
          }).split(',')[0]
        })`;

        allAvailableShifts[shiftDisplayString] = { start: shiftStartDateUtc, end: shiftEndDateUtc };
      }
    });
  });
  return allAvailableShifts;
};

const ShiftAdherence = () => {
  const [adherenceData, setAdherenceData] = useState({});
  const [availableShifts, setAvailableShifts] = useState({});
  const [stationTimeZone, setStationTimeZone] = useState('');
  const [isGroceryEnabled, setIsGroceryEnabled] = useState(false);
  const [isNarrowWindowsEnabled, setIsNarrowWindowsEnabled] = useState(false);

  const titleRef = useRef();
  const [isTitlePopoverOpen, setIsTitlePopoverOpen] = useState(false);

  useEffect(() => {
    (async () => {
      const ssdAdminSettings = await getAdminSettings(getStationCode());
      const narrowWindowsEnabled = await checkNarrowWindowsEnabled();
      const updatedStationTimeZone = chainWalk(() => ssdAdminSettings['timezone'], '');
      const shifts = chainWalk(() => ssdAdminSettings['shifts'], []);
      if (updatedStationTimeZone === '' || shifts.length === 0) {
        return;
      }

      if (ssdAdminSettings[SSD_FEATURE_FLAGS_LIST.groceryEnabled]) {
        setIsGroceryEnabled(true);
      }

      if (narrowWindowsEnabled) {
        setIsNarrowWindowsEnabled(true);
      }

      setStationTimeZone(updatedStationTimeZone);
      const stationTimeZoneOffset = moment.tz(updatedStationTimeZone).format('Z');

      const currDate = new Date();
      const previousDate = new Date(currDate);
      previousDate.setDate(currDate.getDate() - 1);

      let allAvailableShifts;

      if (narrowWindowsEnabled) {
        allAvailableShifts = generateWindowsFromOpsClock(
          ssdAdminSettings,
          currDate,
          previousDate,
          updatedStationTimeZone,
          stationTimeZoneOffset
        );
      } else {
        allAvailableShifts = generateShiftsFromConfig(
          shifts,
          currDate,
          previousDate,
          updatedStationTimeZone,
          stationTimeZoneOffset
        );
      }

      // Sort end times descending.
      const availableEndTimes = [];
      Object.entries(allAvailableShifts).forEach(([_, shiftTimes]) => {
        availableEndTimes.push(shiftTimes['end']);
      });
      availableEndTimes.sort(function (firstDate, secondDate) {
        return secondDate.getTime() - firstDate.getTime();
      });

      // Select latest `NUM_PLANS` plans.
      const filteredEndTimes = availableEndTimes.slice(0, NUM_PLANS);

      // Reconstruct ordered `availableShifts` with latest `NUM_PLANS` plans.
      const updatedAvailableShifts = {};
      filteredEndTimes.forEach((filteredEndTime) => {
        const availableShift = Object.entries(allAvailableShifts).find(([_, shiftTimes]) => {
          return shiftTimes['end'] === filteredEndTime;
        });
        updatedAvailableShifts[availableShift[0]] = availableShift[1];
      });

      setAvailableShifts(updatedAvailableShifts);
    })();
  }, []);

  return (
    <Column spacingInset="400" spacing="500">
      <Row spacing="300">
        <Text type="h500">Headcount Adherence</Text>
        <Button
          type="icon"
          onClick={() => {
            setIsTitlePopoverOpen(!isTitlePopoverOpen);
          }}
          ref={titleRef}>
          <Icon tokens={infoKnockoutTokens} />
        </Button>
        <Popover
          anchorNode={titleRef.current}
          open={isTitlePopoverOpen}
          onClose={() => {
            setIsTitlePopoverOpen(false);
          }}
          position="bottom">
          <Row alignmentVertical="top" spacingInset="100">
            <Text>
              Displays current active Shift Plan for a given site and time period. The plan leverages various inputs
              (Rates, Volumes, Charge) to predict the headcount required for each role in various process paths to
              complete a specified volume in a given amount of time.
            </Text>
            <Button type="icon" onClick={() => setIsTitlePopoverOpen(false)}>
              <Icon tokens={closeSmallTokens} />
            </Button>
          </Row>
        </Popover>
      </Row>

      {Object.keys(availableShifts).length > 0 && (
        <ShiftAdherenceSelector
          adherenceData={adherenceData}
          setAdherenceData={setAdherenceData}
          availableShifts={availableShifts}
          stationTimeZone={stationTimeZone}
          isNarrowWindowsEnabled={isNarrowWindowsEnabled}
        />
      )}

      {Object.keys(adherenceData).length > 0 ? (
        [
          <ShiftAdherenceGraph adherenceData={adherenceData} isGroceryEnabled={isGroceryEnabled} />,
          <ShiftAdherencePlansView adherenceData={adherenceData} />,
          <Divider />,
          <ShiftAdherenceTable adherenceData={adherenceData} isGroceryEnabled={isGroceryEnabled} />,
          <div />
        ]
      ) : (
        <Column alignmentVertical="center" alignmentHorizontal="center" height="50vh">
          <Loader type="circular" size="large" />
        </Column>
      )}
    </Column>
  );
};

export default ShiftAdherence;
